import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi, settingsApi } from '@/services/api';

const AuthContext = createContext(null);

// Fetch and cache organization settings for timezone usage
const fetchAndCacheSettings = async () => {
  try {
    const response = await settingsApi.get();
    const settingsData = {
      company_name: response.data.company_name || '',
      timezone: response.data.timezone || 'America/Chicago',
    };
    localStorage.setItem('orgSettings', JSON.stringify(settingsData));
  } catch (error) {
    // If user doesn't have permission, try public settings
    try {
      const publicResponse = await settingsApi.getPublic();
      const settingsData = {
        company_name: publicResponse.data.company_name || '',
        timezone: publicResponse.data.timezone || 'America/Chicago',
      };
      localStorage.setItem('orgSettings', JSON.stringify(settingsData));
    } catch (e) {
      // Default timezone if unable to fetch
      localStorage.setItem('orgSettings', JSON.stringify({ timezone: 'America/Chicago' }));
    }
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        setIsAuthenticated(true);
        // Fetch settings after auth check
        fetchAndCacheSettings();
      } catch (e) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  };

  const login = async (identifier, password) => {
    const response = await authApi.login({ identifier, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('auth_token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
    setIsAuthenticated(true);
    
    // Fetch and cache settings after login
    fetchAndCacheSettings();
    
    return userData;
  };

  const registerAdmin = async (email, password) => {
    const response = await authApi.registerAdmin({ identifier: email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('auth_token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
    setIsAuthenticated(true);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === 'string') return user.role === roles;
    return roles.includes(user.role);
  };

  // Get token from localStorage for API calls
  const token = localStorage.getItem('auth_token');

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    registerAdmin,
    hasRole,
    token,  // Expose token for portal pages
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
